import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import PageHeader from '../../components/PageHeader';
import EmployeePicker from '../../components/EmployeePicker';
import { apiGet, apiPost } from '../../lib/api';
import { toast } from 'sonner';
import { FileText, Download, Send, Loader2, Mail, Wallet, Plus, X, Save, CheckCircle2 } from 'lucide-react';
import { inrCompact, formatDate } from '../../lib/format';

const DEFAULT_CTC_COMPONENTS = [
  { code: 'BASIC', name: 'Basic Pay', amount_monthly: 0, is_taxable: true },
  { code: 'HRA', name: 'House Rent Allowance', amount_monthly: 0, is_taxable: true },
  { code: 'CON', name: 'Conveyance', amount_monthly: 0, is_taxable: true },
  { code: 'SA', name: 'Special Allowance', amount_monthly: 0, is_taxable: true },
  { code: 'LTA', name: 'LTA', amount_monthly: 0, is_taxable: false },
  { code: 'MED', name: 'Medical Allowance', amount_monthly: 0, is_taxable: true },
];
const REVISION_REASONS = ['Joining', 'Increment', 'Promotion', 'Reorganization', 'Correction'];

export default function HRLetters() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [empId, setEmpId] = useState('');
  const [empMongoId, setEmpMongoId] = useState(''); // emp.id (UUID) — needed for /hr/ctc/{id}
  const [empName, setEmpName] = useState('');
  const [overrides, setOverrides] = useState({});
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [empEmail, setEmpEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [prefilledNotice, setPrefilledNotice] = useState('');
  // inline salary-structure manager
  const [ctcData, setCtcData] = useState({ active: null, history: [] });
  const [ctcLoading, setCtcLoading] = useState(false);
  const [showCtcForm, setShowCtcForm] = useState(false);
  const [ctcForm, setCtcForm] = useState(null);

  // Read ?template= and ?employee= from URL — used by Onboarding redirect
  const urlTemplate = searchParams.get('template') || '';
  const urlEmployee = searchParams.get('employee') || '';

  useEffect(() => {
    apiGet('/hr/letters/templates').then(res => {
      setTemplates(res.templates || []);
      if (res.templates?.length) {
        // Pre-select template from URL if valid, else first
        const fromUrl = urlTemplate && res.templates.find(t => t.id === urlTemplate);
        setTemplateId(fromUrl ? urlTemplate : res.templates[0].id);
      }
    }).catch(() => toast.error('Failed to load templates'));
    // eslint-disable-next-line
  }, [urlTemplate]);

  // Pre-select employee from URL (?employee=<mongo_id>)
  useEffect(() => {
    if (!urlEmployee) return;
    apiGet(`/hr/employees/${urlEmployee}`).then(emp => {
      if (emp?.name) {
        setEmpName(emp.name);
        setEmpId(emp.emp_id || emp.id || '');
        setEmpMongoId(emp.id || '');
        setPrefilledNotice(`Pre-filled from Onboarding for ${emp.name}${emp.emp_id ? ' (' + emp.emp_id + ')' : ''}. Click Preview / Email / PDF to issue the letter.`);
      }
    }).catch(() => {/* employee fetch failed — silent */});
  }, [urlEmployee]);

  // Load active CTC structure whenever a new employee is picked
  useEffect(() => {
    if (!empMongoId) { setCtcData({ active: null, history: [] }); return; }
    setCtcLoading(true);
    apiGet(`/hr/ctc/${empMongoId}`)
      .then(setCtcData)
      .catch(() => setCtcData({ active: null, history: [] }))
      .finally(() => setCtcLoading(false));
  }, [empMongoId]);

  const openCtcForm = () => {
    // Seed the form with the active CTC's components if any, else the default chart of accounts.
    const active = ctcData?.active;
    setCtcForm({
      effective_from: new Date().toISOString().slice(0, 10),
      revision_reason: active ? 'Increment' : 'Joining',
      components: active?.components?.length
        ? DEFAULT_CTC_COMPONENTS.map((d) => {
            const found = active.components.find((c) => c.code === d.code);
            return { ...d, amount_monthly: found?.amount_monthly || 0 };
          })
        : DEFAULT_CTC_COMPONENTS.map((c) => ({ ...c })),
      employer_pf: active?.employer_pf || 0,
      employer_esi: active?.employer_esi || 0,
      gratuity_monthly: active?.gratuity_monthly || 0,
      other_employer_contributions: active?.other_employer_contributions || 0,
      notes: '',
    });
    setShowCtcForm(true);
  };

  const submitCtc = async (e) => {
    e?.preventDefault?.();
    if (!empMongoId || !ctcForm) return;
    try {
      await apiPost('/hr/ctc', {
        employee_id: empMongoId,
        effective_from: ctcForm.effective_from,
        revision_reason: ctcForm.revision_reason,
        components: ctcForm.components.filter(c => (parseFloat(c.amount_monthly) || 0) > 0),
        employer_pf: parseFloat(ctcForm.employer_pf) || 0,
        employer_esi: parseFloat(ctcForm.employer_esi) || 0,
        gratuity_monthly: parseFloat(ctcForm.gratuity_monthly) || 0,
        other_employer_contributions: parseFloat(ctcForm.other_employer_contributions) || 0,
        notes: ctcForm.notes,
      });
      toast.success('Salary structure saved');
      setShowCtcForm(false);
      const refreshed = await apiGet(`/hr/ctc/${empMongoId}`);
      setCtcData(refreshed);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save salary structure');
    }
  };

  const tmpl = templates.find(t => t.id === templateId);

  const setOverride = (k, v) => setOverrides(prev => ({ ...prev, [k]: v }));

  const preview = async () => {
    if (!templateId || !empId) { toast.error('Pick template + employee'); return; }
    setBusy(true);
    try {
      const res = await apiPost('/hr/letters/preview', { template_id: templateId, employee_id: empId, overrides });
      setPreviewHtml(res.html);
      setPreviewSubject(res.subject);
      setEmpEmail(res.employee?.email || res.employee?.contact_email || '');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
    setBusy(false);
  };

  const download = async () => {
    if (!templateId || !empId) { toast.error('Pick template + employee'); return; }
    setBusy(true);
    try {
      const res = await apiPost('/hr/letters/generate', { template_id: templateId, employee_id: empId, overrides });
      // Convert base64 to blob and trigger download
      const bin = atob(res.pdf_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = res.filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Letter downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    }
    setBusy(false);
  };

  const email = async () => {
    if (!templateId || !empId) { toast.error('Pick template + employee'); return; }
    setEmailBusy(true);
    try {
      const res = await apiPost('/hr/letters/email', { template_id: templateId, employee_id: empId, overrides });
      toast.success(`Letter emailed to ${res.to}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email failed');
    }
    setEmailBusy(false);
  };

  return (
    <div className="p-6 space-y-6" data-testid="hr-letters-page">
      <PageHeader title="HR Letter Generator" subtitle="Generate offer / appointment / increment / experience / relieving / salary certificate letters. Preview, download or email directly to the employee." icon={FileText} />

      {prefilledNotice && (
        <div className="bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 rounded-sm text-sm flex items-start gap-2" data-testid="letters-prefilled-banner">
          <FileText size={16} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">{prefilledNotice}</div>
          <button onClick={() => { setPrefilledNotice(''); setSearchParams({}); }} className="text-amber-700 hover:text-amber-900 text-xs font-bold uppercase tracking-wider" data-testid="dismiss-prefill-btn">Dismiss</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-6">
        {/* Left: Letter Builder */}
        <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-4 h-fit">
          <div>
            <label className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Letter Type *</label>
            <select value={templateId} onChange={e => setTemplateId(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="template-select">
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {tmpl && <div className="text-xs text-slate-500 mt-1">{tmpl.description}</div>}
          </div>

          <div>
            <label className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 block mb-1.5">Employee *</label>
            <EmployeePicker
              value={empName}
              onChange={(name, id, emp) => {
                setEmpName(name);
                setEmpId(emp?.emp_id || id || '');
                setEmpMongoId(emp?.id || '');
              }}
              placeholder="Search by name or emp ID"
              testid="letter-employee-picker"
            />
          </div>

          {/* Inline Salary Structure (CTC) panel */}
          {empMongoId && (
            <div className="border border-slate-200 rounded-sm bg-slate-50/60 p-3" data-testid="hrletters-ctc-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-1.5 text-[11px] font-display font-bold uppercase tracking-widest text-slate-600">
                  <Wallet size={12} className="text-[#1F7A6E]" /> Salary Structure
                </div>
                <button
                  type="button"
                  onClick={openCtcForm}
                  className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-bold uppercase tracking-wider border border-[#1F7A6E]/40 text-[#1F7A6E] hover:bg-teal-50 rounded-sm"
                  data-testid="ctc-edit-btn"
                >
                  <Plus size={10} /> {ctcData?.active ? 'New Revision' : 'Create Salary Structure'}
                </button>
              </div>
              {ctcLoading ? (
                <div className="text-[11px] text-slate-400"><Loader2 size={11} className="inline animate-spin mr-1" /> Loading CTC…</div>
              ) : ctcData?.active ? (
                <div className="space-y-1 text-[11px]">
                  <div className="flex justify-between"><span className="text-slate-500">Effective</span><span className="font-display font-semibold text-slate-700">{formatDate(ctcData.active.effective_from)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Reason</span><span className="font-display font-semibold text-slate-700">{ctcData.active.revision_reason || '—'}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Gross / month</span><span className="font-display font-bold text-[#1F7A6E]">{inrCompact(ctcData.active.gross_monthly || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">CTC / annum</span><span className="font-display font-bold text-emerald-700">{inrCompact(ctcData.active.ctc_annual || 0)}</span></div>
                  {ctcData.history?.length > 1 && (
                    <div className="text-[10px] text-slate-400 pt-1 italic">{ctcData.history.length} revision(s) on file</div>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1.5">
                  No salary structure on file. Click "Create Salary Structure" to add one before issuing the letter — required for Appointment / Increment / Salary Certificate.
                </div>
              )}
            </div>
          )}

          {/* Letter-specific overrides */}
          {templateId === 'increment_letter' && (
            <>
              <Field label="Previous Annual CTC (₹)" value={overrides.previous_annual_ctc || ''} onChange={v => setOverride('previous_annual_ctc', v)} type="number" />
              <Field label="New Annual CTC (₹)" value={overrides.annual_ctc || ''} onChange={v => setOverride('annual_ctc', v)} type="number" />
              <Field label="Increment %" value={overrides.increment_percent || ''} onChange={v => setOverride('increment_percent', v)} type="number" />
              <Field label="Revision Effective Date" value={overrides.revision_date || ''} onChange={v => setOverride('revision_date', v)} type="date" />
            </>
          )}

          {(templateId === 'experience_letter' || templateId === 'relieving_letter') && (
            <>
              <Field label="Last Working Day" value={overrides.last_working_day || ''} onChange={v => setOverride('last_working_day', v)} type="date" />
              {templateId === 'relieving_letter' && (
                <Field label="Resignation Date" value={overrides.resignation_date || ''} onChange={v => setOverride('resignation_date', v)} type="date" />
              )}
            </>
          )}

          {templateId === 'offer_letter' && (
            <Field label="Tentative Date of Joining" value={overrides.date_of_joining || ''} onChange={v => setOverride('date_of_joining', v)} type="date" />
          )}

          <div className="pt-2 grid grid-cols-3 gap-2">
            <button onClick={preview} disabled={busy} className="px-3 py-2 border border-slate-300 hover:border-[#1F7A6E] hover:text-[#1F7A6E] text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50" data-testid="preview-btn">
              Preview
            </button>
            <button onClick={download} disabled={busy} className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50" data-testid="download-btn">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} PDF
            </button>
            <button onClick={email} disabled={emailBusy} className="inline-flex items-center justify-center gap-1 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs uppercase tracking-wider font-display font-bold rounded-sm disabled:opacity-50" data-testid="email-letter-btn">
              {emailBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Email
            </button>
          </div>
          {empEmail && <div className="text-xs text-slate-500 flex items-center gap-1.5"><Mail size={11} /> Will email to <b>{empEmail}</b></div>}
        </div>

        {/* Right: Preview */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden min-h-[600px]">
          <div className="border-b border-slate-200 px-5 py-3 bg-slate-50">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">Preview</div>
            <div className="font-display font-bold text-sm text-[#0F172A] mt-0.5">{previewSubject || tmpl?.subject || 'Letter Preview'}</div>
          </div>
          <div className="p-8" data-testid="letter-preview">
            {previewHtml ? (
              <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml, {
                // Letter preview is rendered from server-stored HTML;
                // sanitise here so a tampered template body can't execute JS in
                // a HR user's session. Keep common formatting tags + tables.
                ALLOWED_TAGS: ['p','br','b','strong','i','em','u','span','div','h1','h2','h3','h4','h5','h6','ul','ol','li','table','thead','tbody','tr','td','th','blockquote','hr','a','img'],
                ALLOWED_ATTR: ['href','src','alt','title','class','style','colspan','rowspan'],
                FORBID_TAGS: ['script','iframe','object','embed','form','input','button','svg'],
                FORBID_ATTR: ['onerror','onload','onclick','onmouseover','onfocus','onblur'],
                ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data:image\/[a-z]+):)/i,
              }) }} />
            ) : (
              <div className="text-center text-sm text-slate-400 py-20">Pick a template + employee and click Preview to see the letter.</div>
            )}
          </div>
        </div>
      </div>

      {/* Salary Structure (CTC) form modal */}
      {showCtcForm && ctcForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submitCtc} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl max-h-[90vh] rounded-sm shadow-xl flex flex-col" data-testid="ctc-form-modal">
            <div className="px-5 py-3 border-b border-slate-200 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-[#1F7A6E]">{ctcData?.active ? 'New CTC Revision' : 'Create Salary Structure'}</div>
                <h2 className="font-display font-bold text-lg mt-0.5">{empName}</h2>
                <div className="text-xs text-slate-500">{empId}</div>
              </div>
              <button type="button" onClick={() => setShowCtcForm(false)} className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={18} /></button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Effective From *</label>
                  <input type="date" value={ctcForm.effective_from} onChange={(e) => setCtcForm({ ...ctcForm, effective_from: e.target.value })} required className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" data-testid="ctc-effective" />
                </div>
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Reason *</label>
                  <select value={ctcForm.revision_reason} onChange={(e) => setCtcForm({ ...ctcForm, revision_reason: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                    {REVISION_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1.5">Earnings (Monthly ₹)</div>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                    <tr><th className="text-left px-3 py-1.5 font-display font-bold">Component</th><th className="text-right px-3 py-1.5 font-display font-bold w-40">Amount / mo</th></tr>
                  </thead>
                  <tbody>
                    {ctcForm.components.map((c, idx) => (
                      <tr key={c.code} className="border-t border-slate-100">
                        <td className="px-3 py-1.5"><div className="font-display font-semibold">{c.name}</div><div className="text-[10px] font-mono text-slate-400">{c.code}</div></td>
                        <td className="px-3 py-1.5">
                          <input
                            type="number" min="0" step="100"
                            value={c.amount_monthly}
                            onChange={(e) => {
                              const next = [...ctcForm.components];
                              next[idx] = { ...next[idx], amount_monthly: parseFloat(e.target.value) || 0 };
                              setCtcForm({ ...ctcForm, components: next });
                            }}
                            className="w-full px-2 py-1 border border-slate-200 rounded-sm text-right font-display font-semibold"
                            data-testid={`ctc-comp-${c.code}`}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-200 bg-slate-50/60">
                      <td className="px-3 py-2 font-display font-bold text-[#0F172A]">Gross Monthly</td>
                      <td className="px-3 py-2 text-right font-display font-bold text-[#1F7A6E]">
                        {inrCompact(ctcForm.components.reduce((s, c) => s + (parseFloat(c.amount_monthly) || 0), 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 mb-1.5">Employer Contributions (Monthly ₹)</div>
                <div className="grid grid-cols-2 gap-3">
                  <CtcEmpField label="Employer PF" value={ctcForm.employer_pf} onChange={(v) => setCtcForm({ ...ctcForm, employer_pf: v })} testid="ctc-employer-pf" />
                  <CtcEmpField label="Other Employer" value={ctcForm.other_employer_contributions} onChange={(v) => setCtcForm({ ...ctcForm, other_employer_contributions: v })} testid="ctc-other-emp" />
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Employer ESI & Gratuity are excluded from CTC per Ellora policy.</p>
              </div>

              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">Notes</label>
                <textarea value={ctcForm.notes} onChange={(e) => setCtcForm({ ...ctcForm, notes: e.target.value })} rows="2" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
            </div>

            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-600">
                <span className="font-display font-bold">CTC / annum:</span>{' '}
                <span className="font-display font-bold text-emerald-700">
                  {inrCompact(
                    (ctcForm.components.reduce((s, c) => s + (parseFloat(c.amount_monthly) || 0), 0)
                      + (parseFloat(ctcForm.employer_pf) || 0)
                      + (parseFloat(ctcForm.other_employer_contributions) || 0)) * 12
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setShowCtcForm(false)} className="px-4 py-1.5 border border-slate-300 rounded-sm text-xs font-display font-bold uppercase tracking-wider hover:bg-slate-50">Cancel</button>
                <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#1F7A6E] hover:bg-[#14504E] text-white text-xs font-display font-bold uppercase tracking-wider rounded-sm" data-testid="ctc-save-btn">
                  <Save size={12} /> Save Salary Structure
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function CtcEmpField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1">{label}</label>
      <input
        type="number" min="0" step="100"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full px-3 py-1.5 border border-slate-200 rounded-sm text-sm text-right font-display font-semibold"
        data-testid={testid}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = 'text' }) {
  return (
    <div>
      <label className="text-xs font-display font-bold uppercase tracking-wider text-slate-600 block mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-[#1F7A6E]" />
    </div>
  );
}
